import React, { useMemo } from 'react';
import { 
  getScheduleForDate, 
  TIME_SLOTS, 
  PRN_SLOT, 
  formatFullDate,
  formatDateIso 
} from '../../utils/calendarUtils';
import {
  Sun,
  SunMedium,
  Moon,
  Clock,
  Pill,
  Syringe,
  Flame,
  CheckCircle2,
  Info,
  CalendarCheck
} from 'lucide-react';
import { normalizeCompoundCategory, formatDosageRange } from '../../utils/sortUtils';

export default function DayView({
  currentDate,
  protocols = [],
  statusFilter = 'Active',
  categoryFilter = 'All',
  searchQuery = '',
  onSelectDay
}) {
  const schedule = useMemo(() => {
    const searchLower = searchQuery.toLowerCase().trim();
    const searchableProtocols = searchLower
      ? protocols.filter(p => 
          (p.name || '').toLowerCase().includes(searchLower) ||
          (p.intendedOutcome || '').toLowerCase().includes(searchLower) ||
          (p.notes || '').toLowerCase().includes(searchLower)
        )
      : protocols;

    return getScheduleForDate(searchableProtocols, currentDate, {
      statusFilter,
      categoryFilter
    });
  }, [currentDate, protocols, statusFilter, categoryFilter, searchQuery]);

  const isToday = formatDateIso(currentDate) === formatDateIso(new Date());

  const getSlotIcon = (slotId) => {
    switch (slotId) {
      case 'AM': return Sun;
      case 'NOON': return SunMedium;
      case 'BEDTIME': return Moon;
      case 'PRN': return Clock;
      default: return Clock;
    }
  };

  const getCategoryTheme = (category) => {
    const cat = normalizeCompoundCategory(category);
    switch (cat) {
      case 'Peptide':
        return {
          border: 'border-purple-500/30',
          badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
          dot: 'bg-purple-400',
          icon: Syringe
        };
      case 'Hormones & AAS':
        return {
          border: 'border-rose-500/30',
          badge: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
          dot: 'bg-rose-400',
          icon: Flame
        };
      case 'Supplement':
      default:
        return {
          border: 'border-sky-500/30',
          badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
          dot: 'bg-sky-400',
          icon: Pill
        };
    }
  };

  const totalDoses = 
    schedule.bySlot.AM.length + 
    schedule.bySlot.NOON.length + 
    schedule.bySlot.BEDTIME.length;

  return (
    <div className="space-y-6">
      
      {/* Day Overview Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800 flex items-center space-x-3">
          <div className="p-2.5 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
            <CalendarCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xl font-bold text-white">{schedule.totalScheduled}</div>
            <div className="text-xs text-slate-400">Total Compounds</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800 flex items-center space-x-3">
          <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <Sun className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xl font-bold text-white">{schedule.bySlot.AM.length}</div>
            <div className="text-xs text-slate-400">Morning (AM)</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800 flex items-center space-x-3">
          <div className="p-2.5 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
            <SunMedium className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xl font-bold text-white">{schedule.bySlot.NOON.length}</div>
            <div className="text-xs text-slate-400">Midday (Noon)</div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-slate-900/60 border border-slate-800 flex items-center space-x-3">
          <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
            <Moon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-xl font-bold text-white">{schedule.bySlot.BEDTIME.length}</div>
            <div className="text-xs text-slate-400">Evening / Bedtime</div>
          </div>
        </div>
      </div>

      {/* 3 Main Time Slot Blocks */}
      <div className="space-y-6">
        {TIME_SLOTS.map(slot => {
          const SlotIcon = getSlotIcon(slot.id);
          const slotProtocols = schedule.bySlot[slot.id] || [];

          return (
            <div
              key={slot.id}
              className="p-5 sm:p-6 rounded-3xl bg-slate-900/40 border border-slate-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <div className="flex items-center space-x-3">
                  <span className={`p-2 rounded-xl ${slot.bgColor} ${slot.textColor} border ${slot.borderColor}`}>
                    <SlotIcon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-bold text-white">
                      {slot.label}
                    </h3>
                    <p className="text-xs text-slate-400">
                      Recommended window: {slot.timeRange}
                    </p>
                  </div>
                </div>

                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                  {slotProtocols.length} {slotProtocols.length === 1 ? 'compound' : 'compounds'}
                </span>
              </div>

              {slotProtocols.length === 0 ? (
                <div className="p-6 rounded-2xl bg-slate-950/30 border border-dashed border-slate-800/80 text-center text-xs text-slate-500">
                  No compounds scheduled for {slot.shortLabel.toLowerCase()} on this date.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {slotProtocols.map((protocol, idx) => {
                    const theme = getCategoryTheme(protocol.category);
                    const CategoryIcon = theme.icon;
                    const dosageDisplay = formatDosageRange(protocol);

                    return (
                      <div
                        key={`${protocol.id || protocol.name}-${idx}`}
                        className="p-4 rounded-2xl bg-slate-950/80 border border-slate-800 hover:border-slate-700 transition flex flex-col justify-between space-y-3"
                      >
                        <div>
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-bold text-sm sm:text-base text-white">
                              {protocol.name}
                            </h4>
                            <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0 ${theme.badge}`}>
                              <CategoryIcon className="w-3 h-3" />
                              <span>{normalizeCompoundCategory(protocol.category)}</span>
                            </span>
                          </div>

                          <div className="flex items-center flex-wrap gap-2 mt-2.5">
                            <span className="px-2.5 py-1 rounded-xl text-xs font-bold bg-sky-500/10 text-sky-300 border border-sky-500/30">
                              {dosageDisplay}
                            </span>
                            {protocol.administrationFormat && (
                              <span className="px-2 py-1 rounded-xl text-xs font-medium bg-slate-900 text-slate-300 border border-slate-800">
                                {protocol.administrationFormat}
                              </span>
                            )}
                          </div>

                          <div className="mt-2 text-xs text-slate-400 font-mono">
                            Frequency: {protocol.frequencySchedule || 'Daily'}
                          </div>
                        </div>

                        {(protocol.intendedOutcome || protocol.notes) && (
                          <div className="pt-2.5 border-t border-slate-800/80 text-xs text-slate-400 space-y-1">
                            {protocol.intendedOutcome && (
                              <p className="line-clamp-2">
                                <span className="text-slate-300 font-medium">Goal:</span> {protocol.intendedOutcome}
                              </p>
                            )}
                            {protocol.notes && (
                              <p className="line-clamp-2 text-slate-500 italic">
                                <span className="text-slate-400 not-italic font-medium">Note:</span> {protocol.notes}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* PRN / Flexible Section */}
        {schedule.bySlot.PRN.length > 0 && (
          <div className="p-5 sm:p-6 rounded-3xl bg-slate-900/30 border border-slate-800 space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <div className="flex items-center space-x-3">
                <span className={`p-2 rounded-xl ${PRN_SLOT.bgColor} ${PRN_SLOT.textColor} border ${PRN_SLOT.borderColor}`}>
                  <Clock className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="text-base font-bold text-white">
                    {PRN_SLOT.label}
                  </h3>
                  <p className="text-xs text-slate-400">
                    Non-fixed dosing taken as required or per protocol cycle
                  </p>
                </div>
              </div>

              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 border border-slate-700">
                {schedule.bySlot.PRN.length} items
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {schedule.bySlot.PRN.map((protocol, idx) => {
                const theme = getCategoryTheme(protocol.category);
                const CategoryIcon = theme.icon;
                const dosageDisplay = formatDosageRange(protocol);

                return (
                  <div
                    key={`prn-${protocol.id || protocol.name}-${idx}`}
                    className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-bold text-sm text-white">{protocol.name}</h4>
                      <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${theme.badge}`}>
                        <CategoryIcon className="w-3 h-3" />
                        <span>{normalizeCompoundCategory(protocol.category)}</span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs">
                      <span className="font-bold text-emerald-400">{dosageDisplay}</span>
                      <span className="text-slate-500">•</span>
                      <span className="text-slate-400">{protocol.timingOfDay || 'PRN'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
